<template>
  <div class="ki-partial-container">
    <div v-if="loading" class="ki-loading">
      <spinner />
    </div>
    <div v-else-if="error" class="ki-error">
      <div class="ki-title">{{ $t('error') }}</div>
      <div class="ki-subtitle">{{ error }}</div>
      <main-button flat outline color="#333" :text="$t('back')" @click="goToFailed" />
    </div>
    <div v-else class="ki-content">
      <div class="ki-title-container">
        <div class="ki-title">{{ $t('kiPartialPaymentTitle') }}</div>
        <div class="ki-subtitle">
          {{ $t('kiPartialPaymentDescription') }}
        </div>
      </div>

      <div class="ki-amounts-container">
        <div class="ki-amount-row">
          <span class="ki-amount-label">{{ $t('kiTotalOrderAmount') }}</span>
          <span class="ki-amount-value">{{ totalPrice }} EUR</span>
        </div>
        <div class="ki-amount-row ki-amount-approved">
          <span class="ki-amount-label">{{ $t('kiApprovedAmount') }}</span>
          <span class="ki-amount-value">{{ kiAutoriziraniIznos }} EUR</span>
        </div>
        <div class="ki-divider" />
        <div class="ki-amount-row ki-amount-remainder">
          <span class="ki-amount-label">{{ $t('kiCardPaymentAmount') }}</span>
          <span class="ki-amount-value ki-remainder-value">{{ kiRemainderAmount }} EUR</span>
        </div>
      </div>

      <div v-if="existingProvider">
        {{ $t('cardPayment') }} – {{ existingProvider }}
      </div>
      <div v-else>
        <p
          v-if="!cardMethods.length"
          role="alert"
        >
          {{ $t(catalogError ? 'paymentMethodsError' : 'paymentMethodsUnavailable') }}
        </p>
        <v-radio-group v-model="selectedProvider">
          <v-radio
            v-for="option in cardMethods"
            :key="option.id"
            :value="option.provider"
            :label="`${$t('cardPayment')} – ${option.label}`"
          />
        </v-radio-group>
      </div>
      <div class="ki-actions">
        <main-button
          depressed
          :loading="submitting"
          :disabled="submitting || !selectedProvider"
          :text="$t('kiContinueToCardPayment')"
          @click="proceedToCardPayment"
        />
        <main-button
          flat
          outline
          color="#666"
          :loading="cancelling"
          :disabled="cancelling"
          :text="$t('kiCancelPurchase')"
          @click="cancelKiPayment"
        />
      </div>
    </div>
  </div>
</template>

<script>
import MainButton from '../shared/main-button'
import Spinner from '../shared/spinner'
import { getKiPartialPaymentDetails, cancelKiPayment, getPaymentMethods } from '../../http'
import { eligiblePaymentMethods } from '@/payment-methods'
import { startPaymentCheckout } from '../../utils'

export default {
  name: 'ki-partial-payment',
  components: { MainButton, Spinner },
  props: {
    orderUUID: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      error: null,
      totalPrice: '0.00',
      kiAutoriziraniIznos: '0.00',
      kiRemainderAmount: '0.00',
      paymentData: null,
      cardMethods: [],
      selectedProvider: null,
      existingProvider: null,
      catalogError: false,
      submitting: false,
      cancelling: false,
    }
  },
  async mounted() {
    await this.fetchDetails()
  },
  methods: {
    async fetchDetails() {
      try {
        const { data } = await getKiPartialPaymentDetails(this.orderUUID, { detailsOnly: true })
        const priceMatch = (data.totalPrice || '').match(/[\d.]+/)
        this.totalPrice = priceMatch ? parseFloat(priceMatch[0]).toFixed(2) : '0.00'
        this.kiAutoriziraniIznos = parseFloat(data.kiAutoriziraniIznos || 0).toFixed(2)
        this.kiRemainderAmount = parseFloat(data.kiRemainderAmount || 0).toFixed(2)
        this.existingProvider = data.existingProvider || null
        this.selectedProvider = this.existingProvider
        if (!this.existingProvider) {
          try {
            const catalog = await getPaymentMethods()
            this.cardMethods = eligiblePaymentMethods(catalog).filter(option => option.method === 'CreditCard')
            this.selectedProvider = this.cardMethods.length ? this.cardMethods[0].provider : null
          } catch (error) {
            this.catalogError = true
          }
        }
        this.loading = false
      } catch (err) {
        console.error('Failed to fetch KI partial payment details:', err)
        this.error = this.$t('kiPaymentDetailsError')
        this.loading = false
      }
    },
    async proceedToCardPayment() {
      if (!this.selectedProvider || this.submitting) return
      this.submitting = true
      try {
        if (!this.paymentData) {
          const { data } = await getKiPartialPaymentDetails(this.orderUUID, { provider: this.selectedProvider })
          this.paymentData = data.payment
        }
        await startPaymentCheckout(this.paymentData)
      } catch (error) {
        this.error = this.$t('kiPaymentDetailsError')
      } finally {
        this.submitting = false
      }
    },
    async cancelKiPayment() {
      if (this.cancelling) return
      this.cancelling = true
      try {
        await cancelKiPayment(this.orderUUID)
        this.$router.push({ path: `/canceled-payment/${this.orderUUID}` })
      } catch (err) {
        console.error('Failed to cancel KI payment:', err)
        this.error = this.$t('kiCancelPaymentError')
        this.cancelling = false
      }
    },
    goToFailed() {
      this.$router.push({ path: `/failed-payment/${this.orderUUID}` })
    },
  },
}
</script>

<style scoped>
.ki-partial-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  min-height: 60vh;
}

.ki-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
}

.ki-content {
  width: 100%;
  max-width: 520px;
}

.ki-title-container {
  margin-bottom: 2rem;
}

.ki-title {
  font-family: Raleway, sans-serif;
  font-weight: 800;
  font-size: 28px;
  line-height: 120%;
  color: #333;
  margin-bottom: 0.5rem;
}

.ki-subtitle {
  font-family: 'Source Sans Pro', sans-serif;
  font-weight: 400;
  font-size: 18px;
  line-height: 150%;
  color: #666;
}

.ki-amounts-container {
  background-color: #f5f5f5;
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 2rem;
}

.ki-amount-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
}

.ki-amount-label {
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 16px;
  color: #333;
}

.ki-amount-value {
  font-family: Raleway, sans-serif;
  font-weight: 600;
  font-size: 16px;
  color: #333;
}

.ki-amount-approved {
  color: #2e7d32;
}

.ki-amount-approved .ki-amount-value {
  color: #2e7d32;
}

.ki-divider {
  height: 1px;
  background: rgba(151, 151, 151, 0.5);
  margin: 0.5rem 0;
}

.ki-amount-remainder .ki-amount-label {
  font-weight: 600;
}

.ki-remainder-value {
  font-size: 20px;
  font-weight: 700;
}

.ki-actions {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.ki-error {
  text-align: center;
  max-width: 520px;
}

.ki-error .ki-title {
  margin-bottom: 1rem;
}

.ki-error .ki-subtitle {
  margin-bottom: 2rem;
}

@media (max-width: 480px) {
  .ki-title {
    font-size: 24px;
  }

  .ki-subtitle {
    font-size: 16px;
  }
}
</style>
